import { employeeMappingApi } from 'Api/employeeMapping';
import { timeLogsAssignmentApi } from 'Api/timeLogsAssignment';
import useDebounce from 'components/Global/Hooks/UseDebounce';
import dayjs from 'dayjs';
import { TablePagination, TableSorter } from 'interfaces/global.interface';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CloseSvg } from 'utils/svgs';
import {
	invalidText,
	minutesToTimeString,
	timeStringToMinutes,
	toastText,
} from 'utils/utils';
import TimeLogHeader from './Header';
import styles from './index.module.scss';
import LogAssignmentTable from './LogAssignmentTable';
import {
	CalendarOutlined,
	ExclamationCircleOutlined,
	GiftOutlined,
} from '@ant-design/icons';
import { Tooltip, Tag, Switch, Modal, UploadFile, Upload } from 'antd';
import Buttons from 'components/Global/Buttons';
import { getApi } from 'apis';
import { timeLogsApi } from 'Api/timeLogs';

const TimeLogAssignmentComponent = () => {
	const navigate = useNavigate();
	const queryParams = new URLSearchParams(window.location.search);
	const supervisorFromQuery = queryParams.get('supervisor');
	const dateFromQuery = queryParams.get('date');
	const _isDateDisable = queryParams.get('isDateDisable');
	const location = useLocation();
	const [timeLogsData, setTimeLogsData] = useState<any[]>([]);
	const [totalRecords, setTotalRecords] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(() => {
		const savedSize = localStorage.getItem('pageSize');
		return savedSize ? parseInt(savedSize, 10) : 51;
	});
	const [realTotalRecords, setRealTotalRecords] = useState(0);

	const [date, setDate] = useState<any>(
		dateFromQuery ? dayjs(dateFromQuery) : dayjs()
	);
	const [isDateDisable] = useState<boolean>(
		_isDateDisable === 'true' ? true : false
	);
	const [supervisorFilterValue, setSupervisorFilterValue] = useState(
		supervisorFromQuery || ''
	);

	const [shiftTypeFilterValue, setShiftTypeFilterValue] = useState('');
	const debouncedSearchValue = useDebounce(searchValue, 500);
	const [isSupervisorsLoading, setIsSupervisorsLoading] = useState(false);
	const [supervisorOptions, setSupervisorOptions] = useState<any[]>([]);
	const [shiftOptions, setShiftOptions] = useState<any[]>([]);
	const [isSunday, setIsSunday] = useState(false);
	const [holiday, setHoliday] = useState('');

	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [pendingToggleState, setPendingToggleState] = useState(false);
	const [isSundayRegularHours, setIsSundayRegularHours] = useState(false);
	const [uploadedFile, setUploadedFile] = useState<any>(null);
	const [isLastPageSaved, setIsLastPageSaved] = useState(false);
	const [fetchedFile, setFetchedFile] = useState<any>(null);
	const [fileDocUrl, setFileDocUrl] = useState<any>(null);
	const [isDataChanged, setIsDataChanged] = useState(false);
	// New state for close confirmation modal
	const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
	const [localChanges, setLocalChanges] = useState(new Map());
	const fromPath = location.state?.from || '/monthly-summary';
	const [selectedApplyAllShift, setSelectedApplyAllShift] =
		useState<string>('');
	const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
	const [sortField, setSortField] = useState('');
	const [sortOrder, setSortOrder] = useState('');

	const handleSearch = (value: string) => {
		setSearchValue(value);
		setCurrentPage(1);
	};

	const fetchUploadedFile = async () => {
		try {
			const query = {
				date: dayjs(date).format('YYYY-MM-DD'),
				supervisorId: supervisorFilterValue,
			};
			const response = await getApi('/timeLogs/attachment', query);

			if (response?.data?.data?.name) {
				const file = {
					name: response.data.data.name,
					status: 'done',
				};

				setFileDocUrl(response.data.data.docUrl);
				setUploadedFile([file]);
				setFetchedFile(file); // Update fetched file details
			} else {
				setUploadedFile([]);
				setFetchedFile(null); // Clear fetched file details
			}
		} catch (err) {
			//error
		}
	};
	const handlePageSizeChange = (newPageSize: any) => {
		localStorage.setItem('pageSize', newPageSize.toString());
		setPageSize(newPageSize);
		setCurrentPage(1);
	};

	const uploadFile = async (options: any) => {
		const { onSuccess, onError, file } = options;

		const formattedDate = dayjs(date).format('YYYY-MM-DD');
		const fmData = new FormData();

		fmData.append('date', formattedDate);
		fmData.append('supervisorId', supervisorFilterValue);
		fmData.append('moduleName', 'TIMELOGS');
		fmData.append('file', file);

		try {
			setUploadedFile([file]);
			onSuccess(null, file);
			toastText(`file uploaded successfully`, 'success');
		} catch (error) {
			onError(error);
			toastText(`file upload failed`, 'error');
		}
	};

	const uploadProps: any = {
		customRequest: uploadFile,
		showUploadList: {
			showRemoveIcon: false,
			showPreviewIcon: true,
			showDownloadIcon: false,
		},
		fileList: uploadedFile,
		beforeUpload: (file: UploadFile) => {
			if (!file) {
				return;
			}
			const isValidType =
				file.type &&
				(file.type === 'application/pdf' ||
					file?.type.startsWith('image/'));
			if (!isValidType) {
				toastText('Only PDF and image files are allowed!', 'error');
				return Upload.LIST_IGNORE;
			}
			const isLt1M = file.size! / 1024 / 1024 < 5;
			if (!isLt1M) {
				toastText('File must be smaller than 5MB!', 'error');
				return Upload.LIST_IGNORE;
			}

			setUploadedFile([file]);
			setFileDocUrl(null);
			return false;
		},
		onChange(info: any) {
			if (info.file.status !== 'uploading') {
				if (info.file.status === 'done') {
					setUploadedFile([info.file]);
				} else if (info.file.status === 'error') {
					// Handle error, if needed
				}
			}
		},
		progress: {
			strokeColor: {
				'0%': '#108ee9',
				'100%': '#87d068',
			},
			strokeWidth: 3,
			format: (percent: number) =>
				percent && `${parseFloat(percent.toFixed(2))}%`,
		},
	};
	const handleDatePicker = (value: any) => {
		setDate(value);
		setCurrentPage(1);
		setSelectedApplyAllShift('');
		setLocalChanges(new Map());
		setTimeLogsData([]);
	};

	const handleSupervisorFilter = (value: string) => {
		setSupervisorFilterValue(value);
		setCurrentPage(1);
		setSelectedApplyAllShift('');
	};

	const handleSearchApi = async (value: string) => {
		//search api
	};

	const tableChangeHandler = (
		pagination: TablePagination,
		filters: any,
		sorter: TableSorter
	) => {
		let order = '';
		if (sorter?.order === 'ascend') {
			order = 'asc';
		} else if (sorter?.order) {
			order = 'desc';
		}
		setSortOrder(order);
		setSortField(sorter.field);
	};

	const handleApplyShiftToAll = () => {
		if (!selectedApplyAllShift) return;

		const selectedShift = shiftOptions.find(
			(opt: any) => opt.value === selectedApplyAllShift
		);

		const updatedShiftDetails = {
			shiftType: selectedApplyAllShift,
			shiftName: selectedShift?.shiftName || selectedApplyAllShift,
			shiftId: selectedShift?.id,
		};

		// Update all non-total rows that are not payroll locked
		const updatedEmployees = timeLogsData
			.filter((log) => !log.isTotal)
			.map((item: any) => {
				if (
					item.isPayrollRun ||
					(item.originalSupervisorInfo &&
						!item.originalSupervisorInfo.canEdit)
				) {
					return item; // keep unchanged
				}
				// Check if this employee has manual total hours changes
				const existingLocalChanges =
					localChanges.get(item.employeeId) || {};
				const hasManualTotalHoursChange =
					existingLocalChanges.hasManualTotalHours === true;

				let updatedItem = {
					...item,
					shiftDetails: updatedShiftDetails,
				};

				// If user hasn't manually changed total hours, apply shift defaults
				if (!hasManualTotalHoursChange) {
					const shouldTreatAsOvertime =
						isSunday && !isSundayRegularHours;

					// Don't change totalHours if shouldTreatAsOvertime is true
					if (shouldTreatAsOvertime) {
						updatedItem.timeShiftHours =
							selectedApplyAllShift === 'NIGHT' &&
							item.leaveDetails?.isPaidLeave
								? '00:00'
								: item.totalHours; // Keep existing totalHours
					} else {
						const shiftDefaults = shiftOptions.find(
							(shift) => shift.value === selectedApplyAllShift
						);
						if (shiftDefaults) {
							const defaultTotalMinutes =
								shiftDefaults.defaultRegularHours +
								shiftDefaults.defaultOverTimeHours;
							const defaultTotalHours =
								minutesToTimeString(defaultTotalMinutes);

							let calculatedRegular = '00:00';
							let calculatedOvertime = '00:00';
							const REGULAR_HOURS = item.isForeman ? 720 : 480;

							const regularMinutes = Math.min(
								defaultTotalMinutes,
								REGULAR_HOURS
							);
							const overtimeMinutes = Math.max(
								0,
								defaultTotalMinutes - REGULAR_HOURS
							);
							calculatedRegular =
								minutesToTimeString(regularMinutes);
							calculatedOvertime =
								minutesToTimeString(overtimeMinutes);

							updatedItem = {
								...updatedItem,
								totalHours: defaultTotalHours,
								regularHours: calculatedRegular,
								overtimeHours: calculatedOvertime,
								timeShiftHours:
									selectedApplyAllShift === 'NIGHT' &&
									item.leaveDetails?.isPaidLeave
										? '00:00'
										: defaultTotalHours,
							};
						}
					}
				} else {
					// User has manual hours, only update shift-related fields
					updatedItem.timeShiftHours =
						selectedApplyAllShift === 'NIGHT' &&
						item.leaveDetails?.isPaidLeave
							? '00:00'
							: minutesToTimeString(
									timeStringToMinutes(item.regularHours) +
										timeStringToMinutes(item.overtimeHours)
							  );
				}

				// Update local changes for this employee
				setLocalChanges((prev) => {
					const newChanges = new Map(prev);
					const existingChanges =
						newChanges.get(item.employeeId) || {};

					const updatedChanges = {
						...existingChanges,
						shiftDetails: updatedShiftDetails,
					};

					// Only add hour changes if we're updating them and not in overtime mode
					const shouldTreatAsOvertime =
						isSunday && !isSundayRegularHours;
					if (!hasManualTotalHoursChange && !shouldTreatAsOvertime) {
						updatedChanges.totalHours = updatedItem.totalHours;
						updatedChanges.regularHours = updatedItem.regularHours;
						updatedChanges.overtimeHours =
							updatedItem.overtimeHours;
					}

					updatedChanges.timeShiftHours = updatedItem.timeShiftHours;

					newChanges.set(item.employeeId, updatedChanges);
					return newChanges;
				});

				return updatedItem;
			});

		// Recalculate totals
		const totalsRow = calculateTotals(
			updatedEmployees.filter((i: any) => !i.isTotal)
		);
		const finalData = [...updatedEmployees, totalsRow];

		setTimeLogsData(finalData);
		setSelectedApplyAllShift(''); // Reset the apply all selector
		setFilterPopoverVisible(false); // Close the popover
		setIsDataChanged(true); // Mark data as changed
	};

	// Calculate if user is on last page
	// Handle close button click
	const handleCloseClick = () => {
		if (isLastPageSaved || totalRecords === 0 || !isDataChanged) {
			navigate(fromPath);
		} else {
			// If not on last page, show confirmation modal
			setShowCloseConfirmModal(true);
		}
	};

	// Handle close confirmation
	const confirmClose = () => {
		setShowCloseConfirmModal(false);
		navigate(fromPath);
	};

	const cancelClose = () => {
		setShowCloseConfirmModal(false);
	};

	// Function to recalculate totals for given employees
	const calculateTotals = (employees: any[]) => {
		let totalRegularHours = 0;
		let totalTimeShiftHours = 0;
		let totalOvertimeHours = 0;
		let totalHours = 0;

		employees.forEach((item: any) => {
			if (!item.isTotal) {
				totalRegularHours += timeStringToMinutes(
					item.regularHours || '00:00'
				);
				totalOvertimeHours += timeStringToMinutes(
					item.overtimeHours || '00:00'
				);
				totalHours += timeStringToMinutes(item.totalHours || '00:00');

				// Only add to timeShiftHours if this employee has NIGHT shift
				if (
					item.shiftDetails?.shiftType === 'NIGHT' &&
					!item.leaveDetails?.isPaidLeave
				) {
					totalTimeShiftHours += timeStringToMinutes(
						item.timeShiftHours || '00:00'
					);
				}
			}
		});

		return {
			isTotal: true,
			employeeName: 'Total',
			regularHours: minutesToTimeString(totalRegularHours),
			timeShiftHours: minutesToTimeString(totalTimeShiftHours),
			overtimeHours: minutesToTimeString(totalOvertimeHours),
			totalHours: minutesToTimeString(totalHours),
			invalidRegularHours: false,
			invalidTimeShiftHours: false,
			invalidOvertimeHours: false,
		};
	};

	const mergeLocalChangesWithApiData = useCallback(
		(apiData: any[]) => {
			if (localChanges.size === 0) return apiData;

			const mergedData = apiData.map((item) => {
				if (item.isTotal) return item;

				const localChange = localChanges.get(item.employeeId);
				if (localChange) {
					return { ...item, ...localChange };
				}
				return item;
			});

			// Recalculate totals after merging
			const employees = mergedData.filter((item) => !item.isTotal);
			const totalsRow = calculateTotals(employees);
			return [...employees, totalsRow];
		},
		[localChanges]
	);

	const fetchSupervisors = async () => {
		try {
			setIsSupervisorsLoading(true);
			const res = await employeeMappingApi.getSupervisors();
			const supervisors = res?.data.data.data || [];

			const options = supervisors.map((sup: any) => ({
				label:
					sup.user?.fullName ??
					sup.user?.firstName + ' ' + sup.user?.lastName,
				value: sup.userId,
			}));
			if (supervisors.length > 0 && invalidText(supervisorFromQuery)) {
				setSupervisorFilterValue(options[0].value);
			}
			setSupervisorOptions(options);
		} catch (error: any) {
			let message =
				error?.response?.data?.message || 'Failed to fetch supervisors';
			toastText(message, 'error');
		} finally {
			setIsSupervisorsLoading(false);
		}
	};

	const fetchTimeLogsData = async () => {
		try {
			setIsLoading(true);
			const params = {
				date: dayjs(date).format('YYYY-MM-DD'),
				supervisorId: supervisorFilterValue,
				page: currentPage,
				search: debouncedSearchValue, // Don't send search to API, handle filtering locally
				pageSize: pageSize - 1,
				sortBy: sortField,
				sortOrder: sortOrder,
			};

			const res = await timeLogsAssignmentApi.getHourlyLogs(params);
			const responseData = res?.data?.data || {};
			setRealTotalRecords(res?.data?.totalRecords);
			const mergedData = mergeLocalChangesWithApiData(responseData.data);
			setTimeLogsData(mergedData);
			setTotalRecords(res?.data?.total || 0);
			setIsSunday(res.data.data?.isSunday);
			setHoliday(res.data.data?.holidayName);
			setIsSundayRegularHours(res.data.data.isSundayRegularHours);
			// Set shift options from API response
			if (responseData.shiftOptions) {
				const formattedShiftOptions = responseData.shiftOptions.map(
					(shift: any) => ({
						label: `${shift.shiftName} (${shift.shiftType})`,
						value: shift.shiftType,
						shiftId: shift.id,
						...shift,
					})
				);
				setShiftOptions(formattedShiftOptions);

				if (!shiftTypeFilterValue && formattedShiftOptions.length > 0) {
					setShiftTypeFilterValue(formattedShiftOptions[0].value);
				}
			}
		} catch (error: any) {
			let message =
				error?.response?.data?.message || 'Failed to fetch time logs';
			toastText(message, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	// Effect for API calls (excluding search)
	useEffect(() => {
		if (supervisorFilterValue && date) {
			// Only fetch fresh data when non-search filters change
			fetchTimeLogsData();
		}
	}, [
		supervisorFilterValue,
		date,
		currentPage,
		pageSize,
		shiftTypeFilterValue,
		debouncedSearchValue,
		sortOrder,
		sortField,
	]);

	const handleToggleChange = (checked: any) => {
		setPendingToggleState(checked);
		setShowConfirmModal(true);
	};

	const cancelToggle = () => {
		setShowConfirmModal(false);
	};

	const handleLogChange = (id: string, name: string, value: any) => {
		const shouldTreatAsOvertime = isSunday && !isSundayRegularHours;
		setIsDataChanged(true);

		// Find the current employee data
		const currentEmployee = timeLogsData.find(
			(item) => item.employeeId === id
		);
		if (!currentEmployee) return;

		// Check if this employee has any local changes for totalHours
		const existingLocalChanges = localChanges.get(id) || {};
		const hasManualTotalHoursChange =
			existingLocalChanges.hasManualTotalHours === true;

		// Calculate the updated fields based on the change
		let updatedFields = {};

		if (name === 'totalHours') {
			const totalMinutes = timeStringToMinutes(value);
			let calculatedRegular = '00:00';
			let calculatedOvertime = '00:00';
			const REGULAR_HOURS = currentEmployee.isForeman ? 720 : 480;

			if (shouldTreatAsOvertime) {
				calculatedOvertime = value;
			} else {
				const regularMinutes = Math.min(totalMinutes, REGULAR_HOURS);
				const overtimeMinutes = Math.max(
					0,
					totalMinutes - REGULAR_HOURS
				);
				calculatedRegular = minutesToTimeString(regularMinutes);
				calculatedOvertime = minutesToTimeString(overtimeMinutes);
			}

			updatedFields = {
				totalHours: value,
				regularHours: calculatedRegular,
				overtimeHours: calculatedOvertime,
				timeShiftHours: value,
				invalidRegularHours: false,
				invalidTimeShiftHours: false,
				invalidOvertimeHours: false,
				hasManualTotalHours: true, // This is a MANUAL change
			};
		} else if (name === 'shiftDetails') {
			// If user hasn't manually changed total hours, adjust hours based on shift type
			if (!hasManualTotalHoursChange) {
				const shiftDefaults = shiftOptions.find(
					(shift) => shift.value === value.shiftType
				);
				if (shiftDefaults) {
					// Don't change totalHours if shouldTreatAsOvertime is true
					if (shouldTreatAsOvertime) {
						updatedFields = {
							shiftDetails: value,
							timeShiftHours:
								value.shiftType === 'NIGHT' &&
								currentEmployee.leaveDetails?.isPaidLeave
									? '00:00'
									: currentEmployee.totalHours, // Keep existing totalHours
							invalidRegularHours: false,
							invalidTimeShiftHours: false,
							invalidOvertimeHours: false,
						};
					} else {
						const defaultTotalMinutes =
							shiftDefaults.defaultRegularHours +
							shiftDefaults.defaultOverTimeHours;
						const defaultTotalHours =
							minutesToTimeString(defaultTotalMinutes);

						let calculatedRegular = '00:00';
						let calculatedOvertime = '00:00';
						const REGULAR_HOURS = currentEmployee.isForeman
							? 720
							: 480;

						const regularMinutes = Math.min(
							defaultTotalMinutes,
							REGULAR_HOURS
						);
						const overtimeMinutes = Math.max(
							0,
							defaultTotalMinutes - REGULAR_HOURS
						);
						calculatedRegular = minutesToTimeString(regularMinutes);
						calculatedOvertime =
							minutesToTimeString(overtimeMinutes);

						updatedFields = {
							shiftDetails: value,
							totalHours: defaultTotalHours,
							regularHours: calculatedRegular,
							overtimeHours: calculatedOvertime,
							timeShiftHours:
								value.shiftType === 'NIGHT' &&
								currentEmployee.leaveDetails?.isPaidLeave
									? '00:00'
									: defaultTotalHours,
							invalidRegularHours: false,
							invalidTimeShiftHours: false,
							invalidOvertimeHours: false,
							// DON'T set hasManualTotalHours here - this is automatic
						};
					}
				}
			} else {
				// User has manually changed total hours, so only update shift-related fields
				updatedFields = {
					shiftDetails: value,
					timeShiftHours:
						value.shiftType === 'NIGHT' &&
						currentEmployee.leaveDetails?.isPaidLeave
							? '00:00'
							: minutesToTimeString(
									timeStringToMinutes(
										currentEmployee.regularHours
									) +
										timeStringToMinutes(
											currentEmployee.overtimeHours
										)
							  ),
					invalidRegularHours: false,
					invalidTimeShiftHours: false,
					invalidOvertimeHours: false,
				};
			}
		} else {
			updatedFields = {
				[name]: value,
				invalidRegularHours: false,
				invalidTimeShiftHours: false,
				invalidOvertimeHours: false,
			};
		}

		// Store the change in localChanges
		setLocalChanges((prev) => {
			const newChanges = new Map(prev);
			const existingChanges = newChanges.get(id) || {};
			newChanges.set(id, { ...existingChanges, ...updatedFields });
			return newChanges;
		});

		// Update the UI data
		const updatedData = timeLogsData.map((item) => {
			if (item.employeeId === id) {
				return { ...item, ...updatedFields };
			} else if (item.isTotal) {
				return item;
			} else {
				return {
					...item,
					invalidRegularHours: false,
					invalidTimeShiftHours: false,
					invalidOvertimeHours: false,
				};
			}
		});

		// Recalculate totals
		const employees = updatedData.filter((item) => !item.isTotal);
		const totalsRow = calculateTotals(employees);
		const finalData = [...employees, totalsRow];

		setTimeLogsData(finalData);
	};

	const confirmToggle = () => {
		setIsSundayRegularHours(pendingToggleState);

		const updatedEmployees = timeLogsData
			.filter((log) => !log.isTotal)
			.map((item: any) => {
				if (
					item.isPayrollRun ||
					(item.originalSupervisorInfo &&
						!item.originalSupervisorInfo.canEdit)
				) {
					return item; // keep unchanged
				}
				const regularHours = item.isForeman ? 720 : 480;
				const updatedItem = {
					...item,
					regularHours: pendingToggleState
						? minutesToTimeString(regularHours)
						: '00:00',
					overtimeHours: pendingToggleState
						? '00:00'
						: minutesToTimeString(0),
				};

				const shiftMins =
					timeStringToMinutes(updatedItem.regularHours) +
					timeStringToMinutes(updatedItem.overtimeHours);

				updatedItem.timeShiftHours = minutesToTimeString(shiftMins);
				updatedItem.totalHours = minutesToTimeString(shiftMins);

				return updatedItem;
			});

		// Recalculate totals
		const totalsRow = calculateTotals(
			updatedEmployees.filter((i: any) => !i.isTotal)
		);
		const finalData = [...updatedEmployees, totalsRow];

		// Update both current and original data
		setTimeLogsData(finalData);

		setShowConfirmModal(false);
	};

	const handleSubmit = async () => {
		try {
			setIsSubmitting(true);
			const totalPages = Math.ceil(totalRecords / pageSize);
			const isLastPage = currentPage >= totalPages;

			const employeeHoursData = timeLogsData
				.filter(
					(item) =>
						!item.isTotal ||
						item.isPayrollRun ||
						(item.originalSupervisorInfo &&
							!item.originalSupervisorInfo.canEdit)
				)
				.map((item) => {
					const regularMinutes = timeStringToMinutes(
						item.regularHours || '00:00'
					);
					const overtimeMinutes = timeStringToMinutes(
						item.overtimeHours || '00:00'
					);

					const attendanceStatus =
						!invalidText(holiday) && overtimeMinutes === 0
							? 'ABSENT'
							: regularMinutes === 0 && overtimeMinutes === 0
							? 'ABSENT'
							: 'PRESENT';

					return {
						employeeId: item.employeeId,
						regularHours: regularMinutes,
						overtimeHours: overtimeMinutes,
						timeShiftHours: timeStringToMinutes(
							item.timeShiftHours || '00:00'
						),
						attendanceStatus: attendanceStatus,
						shiftId:
							item.shiftDetails?.shiftId || item.shiftDetails?.id,
					};
				});

			const submitData = {
				date: dayjs(date).format('YYYY-MM-DD'),
				employeeHoursData,
				isRegularHours: isSundayRegularHours,
				supervisorId: supervisorFilterValue,
			};

			const response = await timeLogsAssignmentApi.createHourlyLogs(
				submitData
			);

			if (response?.data) {
				toastText('Time logs submitted successfully', 'success');
				setLocalChanges(new Map());
			}

			if (isLastPage) {
				setIsLastPageSaved(true);
			}
		} catch (error: any) {
			const message =
				error?.response?.data?.message || 'Failed to submit time logs';
			toastText(message, 'error');
		} finally {
			setIsSubmitting(false);
		}
		if (
			uploadedFile[0] &&
			(!fetchedFile || uploadedFile[0].name !== fetchedFile.name)
		) {
			const formData = new FormData();

			formData.append('date', dayjs(date).format('YYYY-MM-DD'));
			formData.append('supervisorId', supervisorFilterValue);
			formData.append('name', uploadedFile[0].name);
			formData.append('moduleName', 'TIMELOGS');
			formData.append('file', uploadedFile[0]);
			await timeLogsApi.uploadAttachment(formData);
		}
	};

	useEffect(() => {
		fetchUploadedFile();
	}, [date, supervisorFilterValue]);

	const myButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'secondary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: false,
			onclick: () => {
				cancelToggle();
			},
		},
		{
			isLoading: false,
			text: 'Confirm',
			className: 'btn-blue',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: true,
			onclick: () => {
				confirmToggle();
			},
		},
	];

	// Buttons for close confirmation modal
	const closeModalButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'secondary-button',
			fontSize: '1.5rem',
			minWidth: '8rem',
			minHeight: '2rem',
			isSubmit: false,
			onclick: () => {
				cancelClose();
			},
		},
		{
			isLoading: false,
			text: 'Confirm',
			className: 'btn-blue',
			fontSize: '1.5rem',
			minWidth: '8rem',
			minHeight: '2rem',
			isSubmit: true,
			onclick: () => {
				confirmClose();
			},
		},
	];

	useEffect(() => {
		fetchSupervisors();
	}, []);

	return (
		<>
			<div className="modal-animation">
				<div className={styles['time-logs']}>
					<div className={styles['time-logs__header']}>
						<div className={styles['time-logs__header__title']}>
							Time Logs
						</div>
						<div
							className={styles['time-logs__header__actions']}
							onClick={handleCloseClick}
						>
							<CloseSvg />
						</div>
					</div>
					<div className={styles['time-logs__table']}>
						<div className={styles['modal-header']}>
							<div>
								<TimeLogHeader
									searchText={searchValue}
									handleSearch={handleSearch}
									handleSearchApi={handleSearchApi}
									handleDatePicker={handleDatePicker}
									dateValue={date}
									supervisorFilterValue={
										supervisorFilterValue
									}
									handleSupervisorFilter={
										handleSupervisorFilter
									}
									supervisorOptions={supervisorOptions}
									supervisorsLoading={isSupervisorsLoading}
									supervisorFromQuery={!!supervisorFromQuery}
									isDateDisable={isDateDisable}
								/>
							</div>

							<div className={styles['modal-header-filter']}>
								{isSunday && (
									<Tooltip title="All hours on Sunday are considered overtime">
										<Tag
											style={{
												cursor: 'pointer',
												fontSize: '14px',
												padding: '8px',
											}}
											color="#584495"
										>
											<div
												style={{
													display: 'flex',
													gap: '7px',
												}}
											>
												<span>
													<CalendarOutlined />
												</span>
												<span>Sunday</span>
											</div>
										</Tag>
									</Tooltip>
								)}
								{!invalidText(holiday) && (
									<Tooltip title="All hours on Holiday are considered overtime">
										<Tag
											style={{
												cursor: 'pointer',
												fontSize: '14px',
												padding: '8px',
											}}
											color="#584495"
										>
											<div
												style={{
													display: 'flex',
													gap: '7px',
												}}
											>
												<span>
													<GiftOutlined />
												</span>
												<span>{holiday}</span>
											</div>
										</Tag>
									</Tooltip>
								)}
								{isSunday && !holiday && (
									<Tooltip title="Sunday calculation details">
										<div
											className={styles['header-toggle']}
										>
											<Switch
												checked={isSundayRegularHours}
												onChange={handleToggleChange}
												disabled={isLoading}
											/>
											<p
												style={{
													fontSize: '14px',
												}}
											>
												Sunday as Regular Hours
											</p>
										</div>
									</Tooltip>
								)}
							</div>
						</div>
						<LogAssignmentTable
							tableData={timeLogsData}
							setTableData={setTimeLogsData}
							totalRecords={totalRecords}
							currentPage={currentPage}
							pageSize={pageSize}
							tableChangeHandler={tableChangeHandler}
							isLoading={isLoading}
							shiftOptions={shiftOptions}
							handleLogChange={handleLogChange}
							onSave={handleSubmit}
							isSunday={isSunday}
							isSubmitLoading={isSubmitting}
							isHoliday={!invalidText(holiday)}
							isSundayRegularHours={isSundayRegularHours}
							realCurrentRecords={realTotalRecords}
							uploadedFile={uploadedFile}
							fileDocUrl={fileDocUrl}
							uploadProps={uploadProps}
							handlePageSizeChange={handlePageSizeChange}
							setIsDataChanged={setIsDataChanged}
							filterPopoverVisible={filterPopoverVisible}
							setFilterPopoverVisible={setFilterPopoverVisible}
							setSelectedApplyAllShift={setSelectedApplyAllShift}
							handleApplyShiftToAll={handleApplyShiftToAll}
							selectedApplyAllShift={selectedApplyAllShift}
						/>
					</div>
				</div>
			</div>

			{/* Sunday toggle confirmation modal */}
			{showConfirmModal && (
				<Modal
					title={
						<>
							<div className={styles['confirm-modal']}>
								<ExclamationCircleOutlined
									style={{
										color: 'red',
										alignItems: 'center',
									}}
								/>
							</div>
						</>
					}
					className={styles['confirm-modal-container']}
					open={showConfirmModal}
					closable={false}
					maskClosable={false}
					footer={[
						<div
							key={'wrapper'}
							className={styles['confirm-modal__button']}
						>
							<Buttons buttons={myButtons}></Buttons>
						</div>,
					]}
				>
					<p className={styles['confirm-modal-text']}>
						{pendingToggleState ? (
							<p>
								Changing Sunday hours setting will impact
								calculation of hours, <b>overtime hours</b> will
								be set to <b>regular hours</b>.
							</p>
						) : (
							<p>
								Changing Sunday hours setting will impact
								calculation of hours, <b>regular hours</b> will
								be set to <b>overtime hours</b>.
							</p>
						)}
						<p>
							Are you sure you want to proceed with this change?
						</p>
					</p>
				</Modal>
			)}

			{/* Close confirmation modal */}
			{showCloseConfirmModal && (
				<Modal
					className={styles['time-log-close-modal']}
					open={showCloseConfirmModal}
					maskClosable={false}
					closable={false}
					footer={[
						<div
							key="close-footer"
							className={styles['time-log-close-modal__footer']}
						>
							<Buttons buttons={closeModalButtons} />
						</div>,
					]}
				>
					<div className={styles['time-log-close-modal__content']}>
						<div className={styles['time-log-close-modal__header']}>
							<ExclamationCircleOutlined
								style={{ color: '#faad14', fontSize: '24px' }}
							/>
							<span
								className={
									styles['time-log-close-modal__title']
								}
							>
								Unsaved Time Log Changes
							</span>
						</div>

						<div
							className={styles['time-log-close-modal__message']}
						>
							<p>
								Closing now will discard your
								<b> unsaved time log changes. </b>
								Do you want to continue without saving?
							</p>
						</div>
					</div>
				</Modal>
			)}
		</>
	);
};

export default TimeLogAssignmentComponent;
